import React, {
  FC,
  KeyboardEvent,
  MutableRefObject,
  SVGProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposerAlignCenterIcon,
  ComposerAlignLeftIcon,
  ComposerAlignRightIcon,
  ComposerBoldIcon,
  ComposerCheckIcon,
  ComposerItalicIcon,
  ComposerLinkIcon,
  ComposerStrikethroughIcon,
  ComposerUnderlineIcon,
} from 'static';

import { normalizeLinkUrl } from './autoLink';
import {
  StyledLinkForm,
  StyledLinkInput,
  StyledToolbar,
  StyledToolbarButton,
  StyledToolbarDivider,
} from './styled';

/**
 * Formatting bar under the email message.
 * Set this to false to hide it. Keyboard shortcuts such as Ctrl+B still work.
 */
export const FORMAT_TOOLBAR = true;

export const MOD_KEY_LABEL =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? '⌘'
    : 'Ctrl';

type FormatCommand =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikeThrough'
  | 'justifyLeft'
  | 'justifyCenter'
  | 'justifyRight';

type ToolbarItem = {
  command: FormatCommand;
  labelKey: string;
  shortcut?: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
};

const TOOLBAR_GROUPS: ToolbarItem[][] = [
  [
    {
      command: 'bold',
      labelKey: 'email.format.bold',
      shortcut: 'B',
      Icon: ComposerBoldIcon,
    },
    {
      command: 'italic',
      labelKey: 'email.format.italic',
      shortcut: 'I',
      Icon: ComposerItalicIcon,
    },
    {
      command: 'underline',
      labelKey: 'email.format.underline',
      shortcut: 'U',
      Icon: ComposerUnderlineIcon,
    },
    {
      command: 'strikeThrough',
      labelKey: 'email.format.strikethrough',
      Icon: ComposerStrikethroughIcon,
    },
  ],
  [
    {
      command: 'justifyLeft',
      labelKey: 'email.format.alignLeft',
      Icon: ComposerAlignLeftIcon,
    },
    {
      command: 'justifyCenter',
      labelKey: 'email.format.alignCenter',
      Icon: ComposerAlignCenterIcon,
    },
    {
      command: 'justifyRight',
      labelKey: 'email.format.alignRight',
      Icon: ComposerAlignRightIcon,
    },
  ],
];

const queryCommandState = (command: string) => {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
};

interface FormatToolbarProps {
  editorRef: MutableRefObject<HTMLElement | null>;
}

const notifyEditor = (editor: HTMLElement) => {
  editor.dispatchEvent(new Event('input', { bubbles: true }));
};

const selectionInside = (editor: HTMLElement) => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  if (!editor.contains(selection.anchorNode)) return null;
  return selection;
};

const FormatToolbarBar: FC<FormatToolbarProps> = ({ editorRef }) => {
  const { t } = useTranslation();
  const linkInputRef = useRef<HTMLInputElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const linkModeRef = useRef(false);
  const [activeFormats, setActiveFormats] = useState<
    Partial<Record<FormatCommand | 'link', boolean>>
  >({});
  const [linkMode, setLinkMode] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [linkInvalid, setLinkInvalid] = useState(false);

  const getSelectionLink = useCallback((): HTMLAnchorElement | null => {
    const editor = editorRef.current;
    const selection = editor ? selectionInside(editor) : null;
    if (!editor || !selection?.anchorNode) return null;
    const node =
      selection.anchorNode.nodeType === Node.ELEMENT_NODE
        ? (selection.anchorNode as Element)
        : selection.anchorNode.parentElement;
    const link = node?.closest('a');
    return link && editor.contains(link) ? link : null;
  }, [editorRef]);

  const refreshActive = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || !selectionInside(editor)) {
      setActiveFormats({});
      return;
    }
    const anchor = selectionInside(editor)?.anchorNode;
    const anchorElement =
      anchor?.nodeType === Node.ELEMENT_NODE
        ? (anchor as HTMLElement)
        : anchor?.parentElement;
    const editorAlign = window.getComputedStyle(editor).textAlign;
    const nodeAlign = anchorElement
      ? window.getComputedStyle(anchorElement).textAlign
      : editorAlign;
    const aligned = nodeAlign !== editorAlign;

    setActiveFormats({
      bold: queryCommandState('bold'),
      italic: queryCommandState('italic'),
      underline: queryCommandState('underline'),
      strikeThrough: queryCommandState('strikeThrough'),
      justifyLeft: aligned && (nodeAlign === 'left' || nodeAlign === 'start'),
      justifyCenter: aligned && nodeAlign === 'center',
      justifyRight: aligned && (nodeAlign === 'right' || nodeAlign === 'end'),
      link: !!getSelectionLink(),
    });
  }, [editorRef, getSelectionLink]);

  useEffect(() => {
    const onSelectionChange = () => {
      if (linkModeRef.current) return;
      refreshActive();
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () =>
      document.removeEventListener('selectionchange', onSelectionChange);
  }, [refreshActive]);

  useEffect(() => {
    if (linkMode) linkInputRef.current?.focus();
  }, [linkMode]);

  const applyFormat = (command: FormatCommand) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(command, false);
    notifyEditor(editor);
    refreshActive();
  };

  const exitLinkMode = (): Range | null => {
    const range = savedRangeRef.current;
    savedRangeRef.current = null;
    linkModeRef.current = false;
    setLinkMode(false);
    setLinkInvalid(false);
    return range;
  };

  const restoreRange = (range: Range | null) => {
    const editor = editorRef.current;
    editor?.focus();
    const selection = window.getSelection();
    if (!selection || !range) return;
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const cancelLinkMode = () => {
    restoreRange(exitLinkMode());
    refreshActive();
  };

  const openLinkMode = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = selectionInside(editor);
    const range = selection
      ? selection.getRangeAt(0).cloneRange()
      : document.createRange();
    if (!selection) {
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    if (getSelectionLink()) {
      editor.focus();
      document.execCommand('unlink', false);
      notifyEditor(editor);
      refreshActive();
      return;
    }
    savedRangeRef.current = range;
    linkModeRef.current = true;
    setLinkValue('');
    setLinkInvalid(false);
    setLinkMode(true);
  }, [editorRef, getSelectionLink, refreshActive]);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      const editor = editorRef.current;
      if (!editor || !editor.contains(document.activeElement)) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openLinkMode();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editorRef, openLinkMode]);

  const applyLink = () => {
    const editor = editorRef.current;
    if (!editor) return;
    if (!linkValue.trim()) {
      cancelLinkMode();
      return;
    }
    const url = normalizeLinkUrl(linkValue);
    if (!url) {
      setLinkInvalid(true);
      return;
    }
    const range = exitLinkMode();
    restoreRange(range);
    if (range?.collapsed) {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.textContent = linkValue.trim();
      range.insertNode(anchor);
      const selection = window.getSelection();
      range.setStartAfter(anchor);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
    } else {
      document.execCommand('createLink', false, url);
    }
    notifyEditor(editor);
    refreshActive();
  };

  const handleLinkKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      applyLink();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelLinkMode();
    }
  };

  return (
    <StyledToolbar
      role="toolbar"
      aria-label={t('email.format.toolbar')}
      onMouseDown={(event) => event.preventDefault()}
    >
      {linkMode ? (
        <StyledLinkForm>
          <StyledLinkInput
            ref={linkInputRef}
            value={linkValue}
            invalid={linkInvalid}
            placeholder={t('email.format.linkPlaceholder')}
            onChange={(event) => {
              setLinkValue(event.target.value);
              setLinkInvalid(false);
            }}
            onKeyDown={handleLinkKeyDown}
            onBlur={() => {
              if (linkModeRef.current) exitLinkMode();
            }}
            onMouseDown={(event) => event.stopPropagation()}
          />
          <StyledToolbarButton
            type="button"
            aria-label={t('email.format.applyLink')}
            title={t('email.format.applyLink')}
            onClick={applyLink}
          >
            <ComposerCheckIcon />
          </StyledToolbarButton>
        </StyledLinkForm>
      ) : (
        <>
          {TOOLBAR_GROUPS.map((group, index) => (
            <React.Fragment key={group[0].command}>
              {index > 0 && <StyledToolbarDivider />}
              {group.map(({ command, labelKey, shortcut, Icon }) => {
                const label = shortcut
                  ? `${t(labelKey)} (${MOD_KEY_LABEL}+${shortcut})`
                  : t(labelKey);
                return (
                  <StyledToolbarButton
                    key={command}
                    type="button"
                    active={!!activeFormats[command]}
                    aria-label={label}
                    aria-pressed={!!activeFormats[command]}
                    title={label}
                    onClick={() => applyFormat(command)}
                  >
                    <Icon />
                  </StyledToolbarButton>
                );
              })}
            </React.Fragment>
          ))}
          <StyledToolbarDivider />
          <StyledToolbarButton
            type="button"
            active={!!activeFormats.link}
            aria-pressed={!!activeFormats.link}
            aria-label={
              activeFormats.link
                ? t('email.format.removeLink')
                : `${t('email.format.link')} (${MOD_KEY_LABEL}+K)`
            }
            title={
              activeFormats.link
                ? t('email.format.removeLink')
                : `${t('email.format.link')} (${MOD_KEY_LABEL}+K)`
            }
            onClick={openLinkMode}
          >
            <ComposerLinkIcon />
          </StyledToolbarButton>
        </>
      )}
    </StyledToolbar>
  );
};

const FormatToolbar: FC<FormatToolbarProps> = (props) =>
  FORMAT_TOOLBAR ? <FormatToolbarBar {...props} /> : null;

export default FormatToolbar;
